import React from 'react';
import { Radio, Spin, Tag, Typography } from 'antd';
import './EditableCandidateRegimenCard.css';

const { Text } = Typography;

export interface CandidateRegimenMetric {
  key: React.Key;
  label: React.ReactNode;
  value: React.ReactNode;
}

export interface CandidateRegimenEditor {
  key: React.Key;
  label: React.ReactNode;
  control: React.ReactNode;
}

export interface CandidateRegimenPhaseSummary {
  loadingDoseLabel?: React.ReactNode;
  maintenanceDoseLabel?: React.ReactNode;
  loading?: {
    dose_mg?: number;
    count?: number;
    total_dose_mg?: number;
    interval_h?: number | null;
  };
  maintenance?: {
    dose_mg?: number;
    interval_h?: number;
  };
  transitionTimeH?: number | null;
}

export type CandidateRegimenRecalcState = 'idle' | 'loading' | 'error';

interface EditableCandidateRegimenCardProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  metrics: CandidateRegimenMetric[];
  phaseSummary?: CandidateRegimenPhaseSummary;
  editors: CandidateRegimenEditor[];
  evaluationLabel: string;
  evaluationColor?: string;
  selected: boolean;
  modified?: boolean;
  recalcState?: CandidateRegimenRecalcState;
  selectionLabel: string;
  onSelect: () => void;
}

const EditableCandidateRegimenCard: React.FC<EditableCandidateRegimenCardProps> = ({
  title,
  subtitle,
  metrics,
  phaseSummary,
  editors,
  evaluationLabel,
  evaluationColor,
  selected,
  modified = false,
  recalcState = 'idle',
  selectionLabel,
  onSelect,
}) => {
  const displayedEvaluation = recalcState === 'loading'
    ? { label: '重算中', color: 'blue' }
    : recalcState === 'error'
      ? { label: '重算失败', color: 'red' }
      : { label: evaluationLabel, color: evaluationColor };

  return (
    <div
      className={`editable-candidate-regimen-card${selected ? ' editable-candidate-regimen-card--selected' : ''}`}
      onClick={onSelect}
      data-selected={selected ? 'true' : 'false'}
    >
      <div className="editable-candidate-regimen-card__header">
        <Radio
          checked={selected}
          aria-label={selectionLabel}
          onChange={onSelect}
          onClick={(event) => event.stopPropagation()}
        />
        <div className="editable-candidate-regimen-card__identity">
          <div className="editable-candidate-regimen-card__title">{title}</div>
          {subtitle ? (
            <div className="editable-candidate-regimen-card__subtitle">{subtitle}</div>
          ) : null}
        </div>
        <div className="editable-candidate-regimen-card__status-list">
          {modified ? <Tag color="blue">已调整</Tag> : null}
          <Tag color={displayedEvaluation.color}>{displayedEvaluation.label}</Tag>
        </div>
      </div>

      <div
        className="editable-candidate-regimen-card__metrics"
        aria-live="polite"
        aria-busy={recalcState === 'loading'}
      >
        {recalcState === 'loading' ? (
          <div className="editable-candidate-regimen-card__recalc-state">
            <Spin size="small" />
            <Text type="secondary">正在重算 PK/PD、PTA 与评价</Text>
          </div>
        ) : metrics.map((metric) => (
          <div key={metric.key} className="editable-candidate-regimen-card__metric">
            <div className="editable-candidate-regimen-card__metric-label">{metric.label}</div>
            <div className="editable-candidate-regimen-card__metric-value">{metric.value}</div>
          </div>
        ))}
      </div>

      {phaseSummary?.loading?.dose_mg != null && phaseSummary.maintenance?.dose_mg != null ? (
        <div className="editable-candidate-regimen-card__phase-summary" aria-label="负荷期和维持期">
          <span className="editable-candidate-regimen-card__phase-label">负荷方案</span>
          <span className="editable-candidate-regimen-card__phase-value">
            负荷 {phaseSummary.loadingDoseLabel ?? `${phaseSummary.loading.dose_mg} mg`} × {phaseSummary.loading.count || 1} 次
          </span>
          <span className="editable-candidate-regimen-card__phase-label">维持方案</span>
          <span className="editable-candidate-regimen-card__phase-value">
            维持 {phaseSummary.maintenanceDoseLabel ?? `${phaseSummary.maintenance.dose_mg} mg`} q{phaseSummary.maintenance.interval_h || '-'}h
          </span>
          {phaseSummary.transitionTimeH != null ? (
            <>
              <span className="editable-candidate-regimen-card__phase-label">转换时间</span>
              <Text type="secondary" className="editable-candidate-regimen-card__phase-value">{phaseSummary.transitionTimeH} h 起</Text>
            </>
          ) : null}
        </div>
      ) : null}

      <div
        className="editable-candidate-regimen-card__editor-section"
        onClick={(event) => event.stopPropagation()}
      >
        <Text strong className="editable-candidate-regimen-card__editor-heading">调整方案</Text>
        <div className="editable-candidate-regimen-card__editor-grid">
          {editors.map((editor) => (
            <div key={editor.key} className="editable-candidate-regimen-card__editor-field">
              <Text type="secondary" className="editable-candidate-regimen-card__editor-label">
                {editor.label}
              </Text>
              {editor.control}
            </div>
          ))}
        </div>
        {recalcState === 'error' ? (
          <Text type="danger" className="editable-candidate-regimen-card__error">
            重算失败，请调整剂量或输注时间后重试
          </Text>
        ) : null}
      </div>
    </div>
  );
};

export default EditableCandidateRegimenCard;
